import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Explore from './pages/Explore';
import Tournaments from './pages/Tournaments';
import TurfDetail from './pages/TurfDetail';
import MyBookings from './pages/MyBookings';
import Checkout from './pages/Checkout';
import BookingConfirmed from './pages/BookingConfirmed';
import Profile from './pages/Profile';
import VerifyBooking from './pages/VerifyBooking';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import About from './pages/About';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import CustomerAgreement from './pages/CustomerAgreement';
import PartnerAgreement from './pages/PartnerAgreement';
import AccountDeletion from './pages/AccountDeletion';
import indiaLocations from './data/indiaLocations';
import PartnerLogin from './pages/partner/PartnerLogin';
import PartnerRegister from './pages/partner/PartnerRegister';
import PartnerForgotPassword from './pages/partner/PartnerForgotPassword';
import PartnerDashboard from './pages/partner/PartnerDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLogin from './pages/admin/AdminLogin';
import InAppUpdateChecker from './components/InAppUpdateChecker';

function NotFound() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ fontSize: '4rem' }}>🏟</div>
      <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#161616', margin: 0 }}>Page Not Found</h1>
      <p style={{ color: '#98A2B3', fontSize: '1rem', margin: 0 }}>The page you're looking for doesn't exist.</p>
      <a href="/" style={{ marginTop: '0.5rem', background: '#084734', color: '#CEF17B', padding: '12px 28px', borderRadius: '50px', textDecoration: 'none', fontWeight: 700, fontSize: '0.95rem' }}>Go Home</a>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function MainLayout({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
}

export default function App() {
  useEffect(() => {
    const handleWheel = () => {
      if (document.activeElement && document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Partner Portal — No Navbar/Footer */}
            <Route path="/partner" element={<PartnerLogin />} />
            <Route path="/partner/login" element={<PartnerLogin />} />
            <Route path="/partner/register" element={<PartnerRegister />} />
            <Route path="/partner/forgot-password" element={<PartnerForgotPassword />} />
            <Route path="/partner/dashboard" element={<PartnerDashboard />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />

            {/* Main Site — With Navbar/Footer */}
            <Route path="/" element={<MainLayout><Home /></MainLayout>} />
            <Route path="/explore" element={<MainLayout><Explore /></MainLayout>} />
            <Route path="/tournaments" element={<MainLayout><Tournaments /></MainLayout>} />
            <Route path="/about" element={<MainLayout><About /></MainLayout>} />
            <Route path="/contact" element={<MainLayout><ContactUs /></MainLayout>} />
            <Route path="/privacy-policy" element={<MainLayout><PrivacyPolicy /></MainLayout>} />
            <Route path="/terms-and-conditions" element={<MainLayout><TermsAndConditions /></MainLayout>} />
            <Route path="/customer-agreement" element={<MainLayout><CustomerAgreement /></MainLayout>} />
            <Route path="/partner-agreement" element={<MainLayout><PartnerAgreement /></MainLayout>} />
            <Route path="/partner/agreement" element={<MainLayout><PartnerAgreement /></MainLayout>} />
            <Route path="/account-deletion" element={<MainLayout><AccountDeletion /></MainLayout>} />
            <Route path="/turf/:id" element={<MainLayout><TurfDetail /></MainLayout>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Navigate to="/login" replace state={{ tab: 'register' }} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/checkout" element={
              <MainLayout><ProtectedRoute><Checkout /></ProtectedRoute></MainLayout>
            } />
            <Route path="/checkout/:id" element={
              <MainLayout><ProtectedRoute><Checkout /></ProtectedRoute></MainLayout>
            } />
            <Route path="/my-bookings" element={
              <MainLayout><ProtectedRoute><MyBookings /></ProtectedRoute></MainLayout>
            } />
            <Route path="/booking/confirmed" element={
              <MainLayout><ProtectedRoute><BookingConfirmed /></ProtectedRoute></MainLayout>
            } />
            <Route path="/verify-booking/:id" element={<VerifyBooking />} />
            <Route path="/booking/verify/:id" element={<VerifyBooking />} />
            <Route path="/profile" element={
              <MainLayout><ProtectedRoute><Profile /></ProtectedRoute></MainLayout>
            } />
            <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
          </Routes>
          <InAppUpdateChecker />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
